/**
 * GATE-EVIDENCE-ATTACH-1: an evidence item may carry a link and a note in its free-form
 * `content` ({ url, note }). The server checks the same limits (EVIDENCE_LINK_INVALID);
 * the panel checks them before saving and again before it renders a link, because
 * `content` is stored as the author wrote it.
 */
export const EVIDENCE_NAME_MAX = 500;
export const EVIDENCE_URL_MAX = 2048;
export const EVIDENCE_NOTE_MAX = 2000;

/** The link as an absolute http or https URL (at most 2048 characters), or null. */
export function safeHttpUrl(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const raw = value.trim();
  if (!raw || raw.length > EVIDENCE_URL_MAX) return null;
  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    return null;
  }
  return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? raw : null;
}

/** Why a typed link cannot be saved, or null when it can. */
export function evidenceLinkError(value: string): string | null {
  const raw = value.trim();
  if (!raw) return 'Enter a link.';
  if (raw.length > EVIDENCE_URL_MAX) return `The link is longer than ${EVIDENCE_URL_MAX} characters.`;
  return safeHttpUrl(raw) ? null : 'Enter a full link that starts with http:// or https://.';
}

/** The link and note an evidence item carries, each only when usable. */
export function evidenceContent(content: unknown): { url: string | null; note: string | null } {
  const bag = content && typeof content === 'object' && !Array.isArray(content)
    ? (content as Record<string, unknown>)
    : {};
  const note = typeof bag.note === 'string' && bag.note.trim() ? bag.note.trim() : null;
  return { url: safeHttpUrl(bag.url), note };
}
